import * as THREE from 'three';

/**
 * CameraController类 - 管理3D场景的相机控制
 * 
 * 职责:
 * - 处理鼠标拖拽和触摸手势
 * - 实现相机平滑旋转和缩放
 * - 限制相机角度防止翻转
 * - 提供惯性效果
 * 
 * 实现细节:
 * - 使用球坐标系统（spherical coordinates）实现环绕旋转
 * - 俯仰角限制在10-80度之间
 * - 缩放距离限制在20-100单位之间
 * - 使用线性插值（lerp）实现平滑过渡
 * 
 * 验证需求: Requirements 1.3, 3.2, 8.1, 8.2, 8.3, 8.4, 8.5
 */
class CameraController {
  constructor(camera, domElement) {
    if (!camera) {
      throw new Error('Camera is required');
    }
    if (!domElement) {
      throw new Error('DOM element is required');
    }

    this.camera = camera;
    this.domElement = domElement;

    // 球坐标系统参数
    this.spherical = {
      radius: 50,      // 相机到中心的距离
      theta: 0,        // 水平角度（方位角）
      phi: Math.PI / 4 // 垂直角度（俯仰角），初始45度
    };

    // 目标球坐标（用于平滑插值）
    this.targetSpherical = {
      radius: 50,
      theta: 0,
      phi: Math.PI / 4
    };

    // 限制参数
    this.minDistance = 20;
    this.maxDistance = 100;
    this.minPolarAngle = THREE.MathUtils.degToRad(10);  // 10度
    this.maxPolarAngle = THREE.MathUtils.degToRad(80);  // 80度

    // 平滑插值参数
    this.dampingFactor = 0.1; // 阻尼系数，值越小越平滑

    // 旋转灵敏度
    this.rotateSpeed = 0.005;
    this.zoomSpeed = 0.1;

    // 交互状态
    this.isEnabled = false;
    this.isDragging = false;
    this.previousMousePosition = { x: 0, y: 0 };
    this.previousTouchDistance = 0;

    // 场景中心点
    this.target = new THREE.Vector3(0, 0, 0);

    // 绑定事件处理函数的this上下文
    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
    this._onWheel = this._onWheel.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._onContextMenu = this._onContextMenu.bind(this);
  }

  /**
   * 启用相机控制
   */
  enableControls() {
    if (this.isEnabled) return;

    this.isEnabled = true;

    // 鼠标事件
    this.domElement.addEventListener('mousedown', this._onMouseDown, false);
    this.domElement.addEventListener('wheel', this._onWheel, false);
    this.domElement.addEventListener('contextmenu', this._onContextMenu, false);

    // 触摸事件
    this.domElement.addEventListener('touchstart', this._onTouchStart, { passive: false });
    this.domElement.addEventListener('touchmove', this._onTouchMove, { passive: false });
    this.domElement.addEventListener('touchend', this._onTouchEnd, false);
  }

  /**
   * 禁用相机控制
   */
  disableControls() {
    if (!this.isEnabled) return;

    this.isEnabled = false;

    // 移除鼠标事件
    this.domElement.removeEventListener('mousedown', this._onMouseDown);
    this.domElement.removeEventListener('wheel', this._onWheel);
    this.domElement.removeEventListener('contextmenu', this._onContextMenu);

    // 移除触摸事件
    this.domElement.removeEventListener('touchstart', this._onTouchStart);
    this.domElement.removeEventListener('touchmove', this._onTouchMove);
    this.domElement.removeEventListener('touchend', this._onTouchEnd);

    // 移除全局事件监听器
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
  }

  /**
   * 更新相机位置（每帧调用）
   * @param {number} deltaTime - 时间增量（秒）
   */
  update(deltaTime) {
    // 使用线性插值平滑过渡到目标位置
    this.spherical.radius += (this.targetSpherical.radius - this.spherical.radius) * this.dampingFactor;
    this.spherical.theta += (this.targetSpherical.theta - this.spherical.theta) * this.dampingFactor;
    this.spherical.phi += (this.targetSpherical.phi - this.spherical.phi) * this.dampingFactor;

    // 应用限制
    this.spherical.radius = THREE.MathUtils.clamp(
      this.spherical.radius,
      this.minDistance,
      this.maxDistance
    );
    this.spherical.phi = THREE.MathUtils.clamp(
      this.spherical.phi,
      this.minPolarAngle,
      this.maxPolarAngle
    );

    // 从球坐标转换为笛卡尔坐标
    const position = this._sphericalToCartesian(
      this.spherical.radius,
      this.spherical.theta,
      this.spherical.phi
    );

    // 更新相机位置
    this.camera.position.copy(position);
    this.camera.lookAt(this.target);
  }

  /**
   * 旋转相机
   * @param {number} deltaX - 水平旋转增量
   * @param {number} deltaY - 垂直旋转增量
   */
  rotate(deltaX, deltaY) {
    // 更新目标角度
    this.targetSpherical.theta -= deltaX * this.rotateSpeed;
    this.targetSpherical.phi -= deltaY * this.rotateSpeed;

    // 限制俯仰角
    this.targetSpherical.phi = THREE.MathUtils.clamp(
      this.targetSpherical.phi,
      this.minPolarAngle,
      this.maxPolarAngle
    );
  }

  /**
   * 缩放相机
   * @param {number} delta - 缩放增量（正值放大，负值缩小）
   */
  zoom(delta) {
    // 更新目标距离
    this.targetSpherical.radius += delta * this.zoomSpeed;

    // 限制缩放距离
    this.targetSpherical.radius = THREE.MathUtils.clamp(
      this.targetSpherical.radius,
      this.minDistance,
      this.maxDistance
    );
  }

  /**
   * 重置相机到初始位置
   */
  reset() {
    this.targetSpherical.radius = 50;
    this.targetSpherical.theta = 0;
    this.targetSpherical.phi = Math.PI / 4;
  }

  /**
   * 球坐标转笛卡尔坐标
   * @param {number} radius - 半径
   * @param {number} theta - 方位角
   * @param {number} phi - 俯仰角
   * @returns {THREE.Vector3}
   */
  _sphericalToCartesian(radius, theta, phi) {
    const x = radius * Math.sin(phi) * Math.sin(theta);
    const y = radius * Math.cos(phi);
    const z = radius * Math.sin(phi) * Math.cos(theta);
    return new THREE.Vector3(x, y, z).add(this.target);
  }

  /**
   * 鼠标按下事件处理
   * @param {MouseEvent} event
   */
  _onMouseDown(event) {
    if (!this.isEnabled) return;

    event.preventDefault();

    this.isDragging = true;
    this.previousMousePosition = {
      x: event.clientX,
      y: event.clientY
    };

    // 添加全局事件监听器
    document.addEventListener('mousemove', this._onMouseMove, false);
    document.addEventListener('mouseup', this._onMouseUp, false);
  }

  /**
   * 鼠标移动事件处理
   * @param {MouseEvent} event
   */
  _onMouseMove(event) {
    if (!this.isEnabled || !this.isDragging) return;

    event.preventDefault();

    const deltaX = event.clientX - this.previousMousePosition.x;
    const deltaY = event.clientY - this.previousMousePosition.y;

    this.rotate(deltaX, deltaY);

    this.previousMousePosition = {
      x: event.clientX,
      y: event.clientY
    };
  }

  /**
   * 鼠标释放事件处理
   * @param {MouseEvent} event
   */
  _onMouseUp(event) {
    if (!this.isEnabled) return;

    event.preventDefault();

    this.isDragging = false;

    // 移除全局事件监听器
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
  }

  /**
   * 鼠标滚轮事件处理
   * @param {WheelEvent} event
   */
  _onWheel(event) {
    if (!this.isEnabled) return;

    event.preventDefault();

    // 标准化滚轮增量
    const delta = event.deltaY > 0 ? 1 : -1;
    this.zoom(delta);
  }

  /**
   * 触摸开始事件处理
   * @param {TouchEvent} event
   */
  _onTouchStart(event) {
    if (!this.isEnabled) return;

    event.preventDefault();

    if (event.touches.length === 1) {
      // 单指触摸 - 旋转
      this.isDragging = true;
      this.previousMousePosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
    } else if (event.touches.length === 2) {
      // 双指触摸 - 缩放
      this.isDragging = false;
      const dx = event.touches[0].clientX - event.touches[1].clientX;
      const dy = event.touches[0].clientY - event.touches[1].clientY;
      this.previousTouchDistance = Math.sqrt(dx * dx + dy * dy);
    }
  }

  /**
   * 触摸移动事件处理
   * @param {TouchEvent} event
   */
  _onTouchMove(event) {
    if (!this.isEnabled) return;

    event.preventDefault();

    if (event.touches.length === 1 && this.isDragging) {
      // 单指旋转
      const deltaX = event.touches[0].clientX - this.previousMousePosition.x;
      const deltaY = event.touches[0].clientY - this.previousMousePosition.y;

      this.rotate(deltaX, deltaY);

      this.previousMousePosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
    } else if (event.touches.length === 2) {
      // 双指缩放
      const dx = event.touches[0].clientX - event.touches[1].clientX;
      const dy = event.touches[0].clientY - event.touches[1].clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (this.previousTouchDistance > 0) {
        const delta = (this.previousTouchDistance - distance) * 0.1;
        this.zoom(delta);
      }

      this.previousTouchDistance = distance;
    }
  }

  /**
   * 触摸结束事件处理
   * @param {TouchEvent} event
   */
  _onTouchEnd(event) {
    if (!this.isEnabled) return;

    this.isDragging = false;
    this.previousTouchDistance = 0;
  }

  /**
   * 右键菜单事件处理（阻止默认行为）
   * @param {Event} event
   */
  _onContextMenu(event) {
    if (!this.isEnabled) return;
    event.preventDefault();
  }

  /**
   * 获取当前相机距离
   * @returns {number}
   */
  getDistance() {
    return this.spherical.radius;
  }

  /**
   * 获取当前俯仰角（弧度）
   * @returns {number}
   */
  getPolarAngle() {
    return this.spherical.phi;
  }

  /**
   * 获取当前方位角（弧度）
   * @returns {number}
   */
  getAzimuthalAngle() {
    return this.spherical.theta;
  }

  /**
   * 设置相机距离
   * @param {number} distance
   */
  setDistance(distance) {
    this.targetSpherical.radius = THREE.MathUtils.clamp(
      distance,
      this.minDistance,
      this.maxDistance
    );
  }

  /**
   * 设置俯仰角（弧度）
   * @param {number} angle
   */
  setPolarAngle(angle) {
    this.targetSpherical.phi = THREE.MathUtils.clamp(
      angle,
      this.minPolarAngle,
      this.maxPolarAngle
    );
  }

  /**
   * 设置方位角（弧度）
   * @param {number} angle
   */
  setAzimuthalAngle(angle) {
    this.targetSpherical.theta = angle;
  }
}

export default CameraController;
